"use strict";
// chatapp/packages/shared/src/graphql/invoker.ts
//
// NOTE: This file is shared between:
//   - Web (React + Amplify v5)
//   - React Native / Expo (no direct Amplify dependency here)
//
// Important design points:
//   - We DO NOT statically import 'aws-amplify' anywhere.
//   - Web-only code lazily requires 'aws-amplify' inside a function.
//   - React Native gets its ID token via an injected provider.
//   - Subscriptions are implemented via Amplify API.graphql on web only;
//     on RN we currently return a no-op subscription.
Object.defineProperty(exports, "__esModule", { value: true });
exports.GraphqlQuery = void 0;
exports.setReactNativeTokenProvider = setReactNativeTokenProvider;
exports.setReactNativeRegionProvider = setReactNativeRegionProvider;
exports.getIdTokenWithFallback = getIdTokenWithFallback;
exports.extractUserIdFromToken = extractUserIdFromToken;
exports.invokeGraphqlProxy = invokeGraphqlProxy;
exports.SubscribeToAgentAPI = SubscribeToAgentAPI;
const endpoints_js_1 = require("../endpoints.js");
/* -------------------------------------------------
   Environment / platform helpers
--------------------------------------------------*/
function isReactNative() {
    try {
        // Works in Expo/React Native
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (typeof navigator !== "undefined" &&
            navigator?.product === "ReactNative");
    }
    catch {
        return false;
    }
}
function hasDOM() {
    return typeof document !== "undefined" && typeof window !== "undefined";
}
function bearer(token) {
    const t = token.trim();
    if (t.toLowerCase().startsWith("bearer "))
        return t;
    return `Bearer ${t}`;
}
/* -------------------------------------------------
   Web-only Amplify access (lazy, no static import)
--------------------------------------------------*/
let webAmplify = null;
function getWebAmplify() {
    if (isReactNative()) {
        throw new Error("getWebAmplify() should not be called on React Native.");
    }
    if (!webAmplify) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports, global-require
        webAmplify = require("aws-amplify");
    }
    return webAmplify;
}
const PROXY_TIMEOUT_MS = 15000;
/* -------------------------------------------------
   React Native providers
--------------------------------------------------*/
let rnTokenProvider = null;
let rnRegionProvider = null;
function setReactNativeTokenProvider(fn) {
    rnTokenProvider = fn;
}
function setReactNativeRegionProvider(fn) {
    rnRegionProvider = fn;
}
/* -------------------------------------------------
   Token helpers (RN-safe)
--------------------------------------------------*/
function readCookie(name) {
    if (!hasDOM())
        return null;
    try {
        const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return m ? decodeURIComponent(m[1]) : null;
    }
    catch {
        return null;
    }
}
function decodeJwtPayload(jwt) {
    try {
        const part = jwt.split(".")[1] || "";
        const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "===".slice((b64.length + 3) % 4);
        let json = null;
        const g = globalThis;
        if (g && typeof g.atob === "function") {
            json = g.atob(padded);
        }
        else if (typeof Buffer !== "undefined" && Buffer?.from) {
            json = Buffer.from(padded, "base64").toString("utf8");
        }
        else {
            return null;
        }
        if (!json)
            return null;
        return JSON.parse(json);
    }
    catch {
        return null;
    }
}
function isLikelyJwt(token) {
    return !!token && typeof token === "string" && token.startsWith("eyJ");
}
function isIdToken(jwt) {
    const payload = decodeJwtPayload(jwt);
    return !!payload && payload.token_use === "id";
}
function normalizeRegion(raw) {
    const v = (raw || "").trim().toLowerCase();
    if (v === "eu")
        return "eu";
    if (v === "global")
        return "global";
    return null;
}
/** Web-only: get a fresh ID token from Amplify Auth, validated if possible. */
async function getFreshIdTokenFromAmplifyWeb() {
    const { Auth } = getWebAmplify();
    const session = await Auth.currentSession();
    const id = session.getIdToken().getJwtToken();
    if (!isLikelyJwt(id))
        throw new Error("Invalid ID token format.");
    const payload = decodeJwtPayload(id);
    if (payload && payload.token_use !== "id") {
        console.warn("⚠️ getFreshIdTokenFromAmplifyWeb: token_use != 'id' on fresh session. Re-fetching once.");
        const s2 = await Auth.currentSession();
        const id2 = s2.getIdToken().getJwtToken();
        if (!isLikelyJwt(id2))
            throw new Error("Invalid ID token format.");
        const p2 = decodeJwtPayload(id2);
        if (p2 && p2.token_use !== "id") {
            throw new Error("Session token is not an ID token (token_use != 'id').");
        }
        return id2;
    }
    return id;
}
/** RN-only: get ID token from the injected token provider. */
async function getIdTokenFromReactNative() {
    if (!rnTokenProvider) {
        throw new Error("React Native token provider is not configured. " +
            "Call setReactNativeTokenProvider(...) in your mobile app bootstrap.");
    }
    const token = await rnTokenProvider();
    if (!isLikelyJwt(token)) {
        throw new Error("Invalid or missing ID token from React Native token provider.");
    }
    if (!isIdToken(token)) {
        console.warn("⚠️ RN token provider returned a JWT that doesn't look like an ID token (token_use != 'id').");
    }
    return token;
}
/**
 * Get an ID token with environment-specific fallbacks.
 */
async function getIdTokenWithFallback() {
    if (isReactNative()) {
        return getIdTokenFromReactNative();
    }
    try {
        const id = await getFreshIdTokenFromAmplifyWeb();
        try {
            localStorage.setItem("id_token", id);
        }
        catch {
            /* ignore */
        }
        return id;
    }
    catch {
        // continue to fallbacks
    }
    try {
        const ls = localStorage.getItem("id_token") ||
            localStorage.getItem("cognito_id_token");
        if (isLikelyJwt(ls)) {
            if (isIdToken(ls))
                return ls;
            console.warn("⚠️ LocalStorage 'id_token' was not an ID token (token_use != 'id'). Ignoring.");
        }
    }
    catch {
        /* ignore */
    }
    const ck = readCookie("id_token") || readCookie("cognito_id_token");
    if (isLikelyJwt(ck)) {
        if (isIdToken(ck))
            return ck;
        console.warn("⚠️ Cookie 'id_token' was not an ID token (token_use != 'id'). Ignoring.");
    }
    throw new Error("User is not authenticated with a valid ID token.");
}
function extractUserIdFromToken(idToken) {
    const payload = decodeJwtPayload(idToken);
    if (!payload) {
        console.warn("⚠️ extractUserIdFromToken: unable to decode ID token payload in this runtime.");
        return null;
    }
    return typeof payload.sub === "string" ? payload.sub : null;
}
/* -------------------------------------------------
   Region-aware endpoint selection (RN only)
--------------------------------------------------*/
function trimTrailingSlash(url) {
    return url.endsWith("/") ? url.slice(0, -1) : url;
}
function pickEndpointFromRegion(region) {
    if (region === "eu") {
        return trimTrailingSlash(endpoints_js_1.expoGraphqlProxyEu || endpoints_js_1.GRAPHQL_PROXY_EU_DEFAULT);
    }
    return trimTrailingSlash(endpoints_js_1.expoGraphqlProxyGlobal || endpoints_js_1.GRAPHQL_PROXY_GLOBAL_DEFAULT);
}
async function getReactNativeResolvedRegion() {
    if (!isReactNative())
        return null;
    // 1) Provider (SecureStore-backed)
    if (rnRegionProvider) {
        try {
            const r = await rnRegionProvider();
            const norm = normalizeRegion(r);
            if (norm)
                return norm;
        }
        catch {
            /* ignore */
        }
    }
    // 2) Env hint
    const envHint = normalizeRegion(endpoints_js_1.expoMobileRegionHint ?? undefined);
    if (envHint)
        return envHint;
    return null;
}
async function resolveProxyEndpointForThisCall() {
    if (!isReactNative())
        return endpoints_js_1.graphqlProxyEndpoint;
    // Explicit single endpoint override (dev/testing)
    const explicit = (process.env.EXPO_PUBLIC_GRAPHQL_PROXY_ENDPOINT || "").trim();
    if (explicit)
        return trimTrailingSlash(explicit);
    // Prefer region-based endpoints to avoid POST redirects
    const region = await getReactNativeResolvedRegion();
    if (region)
        return pickEndpointFromRegion(region);
    // Last resort fallback
    return endpoints_js_1.graphqlProxyEndpoint;
}
function analyzeGraphqlErrors(errors) {
    const first = Array.isArray(errors) && errors.length > 0 ? errors[0] : undefined;
    const type = first?.errorType ||
        first?.extensions?.errorType ||
        (first?.message?.includes?.("WELCOME_PENDING")
            ? "WELCOME_PENDING"
            : undefined);
    const isWelcomePending = type === "WELCOME_PENDING";
    const message = first?.message ||
        (isWelcomePending
            ? "Need at least hello + welcome; please retry shortly."
            : "GraphQL request failed.");
    return { isWelcomePending, message, type, firstError: first };
}
/* -------------------------------------------------
   Fetch with timeout
--------------------------------------------------*/
async function fetchWithTimeout(resource, init, timeoutMs) {
    const ctrl = new AbortController();
    const id = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        return await fetch(resource, { ...init, signal: ctrl.signal });
    }
    finally {
        clearTimeout(id);
    }
}
/* -------------------------------------------------
   Operation name inference
--------------------------------------------------*/
const OP_RE = /\b(query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/m;
function inferOperationName(doc) {
    if (!doc || !doc.trim())
        return "Passthrough";
    const cleaned = doc
        .replace(/^\uFEFF/, "")
        .replace(/^[\s\r\n]+/, "")
        .replace(/^(\s*#[^\n]*\n)+/g, "");
    const m = cleaned.match(OP_RE);
    return m?.[2] ?? "Passthrough";
}
/* -------------------------------------------------
   Proxy invoker
--------------------------------------------------*/
async function invokeGraphqlProxy(query, variables = {}, options = { forwardAuthMode: "userpools" }) {
    const forwardAuthMode = options.forwardAuthMode ?? "userpools";
    const idToken = await getIdTokenWithFallback();
    const payload = decodeJwtPayload(idToken);
    if (!isReactNative()) {
        if (!payload || payload.token_use !== "id") {
            console.error("❌ invokeGraphqlProxy: Non-ID token detected in Authorization.", { token_use: payload?.token_use });
            throw new Error("Authorization token is not an ID token (token_use != 'id').");
        }
    }
    const opName = inferOperationName(query);
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: bearer(idToken),
        "x-appsync-auth-mode": forwardAuthMode,
        "x-graphql-op": opName,
    };
    if (payload?.sub)
        headers["x-user-sub"] = payload.sub;
    const body = JSON.stringify({ query, variables, operationName: opName });
    const endpoint = await resolveProxyEndpointForThisCall();
    let responseBody = null;
    try {
        console.debug("[proxy] → POST /graphql", {
            endpoint,
            forwardAuthMode,
            opName,
            hasAuthHeader: !!headers.Authorization,
            rn: isReactNative(),
        });
        const resp = await fetchWithTimeout(endpoint, {
            method: "POST",
            headers,
            body,
            ...(hasDOM() ? { credentials: "include" } : {}),
            mode: hasDOM() ? "cors" : undefined,
        }, PROXY_TIMEOUT_MS);
        const rawText = await resp.text();
        try {
            responseBody = rawText ? JSON.parse(rawText) : {};
        }
        catch {
            responseBody = { errors: [{ message: "Non-JSON response from proxy" }] };
        }
        if (!resp.ok) {
            const firstErrMsg = Array.isArray(responseBody?.errors) && responseBody.errors.length > 0
                ? responseBody.errors[0]?.message
                : undefined;
            const msg = responseBody?.message ||
                firstErrMsg ||
                `HTTP ${resp.status} ${resp.statusText}`;
            console.error("❌ Proxy HTTP error:", msg);
            throw new Error(msg);
        }
        if (responseBody?.errors) {
            const { isWelcomePending, message } = analyzeGraphqlErrors(responseBody.errors);
            if (isWelcomePending && forwardAuthMode === "iam") {
                console.debug("⏳ [proxy] WELCOME_PENDING (private). Returning empty data to let pollers retry.");
                return {};
            }
            console.error("❌ Proxy GraphQL error:", responseBody.errors);
            const err = new Error(message);
            err.graphQLErrors = responseBody.errors;
            throw err;
        }
        return responseBody.data;
    }
    catch (err) {
        if (err?.name === "AbortError") {
            console.error("⏱️ Proxy request timed out:", {
                timeoutMs: PROXY_TIMEOUT_MS,
            });
        }
        else {
            console.error("❌ Failed to invoke GraphQL proxy:", err);
        }
        throw err;
    }
}
/* -------------------------------------------------
   Lightweight wrapper
--------------------------------------------------*/
class GraphqlQuery {
    constructor(query, optsOrBool = {
        forwardAuthMode: "userpools",
    }) {
        this.query = query;
        if (typeof optsOrBool === "boolean") {
            this.options = optsOrBool
                ? { forwardAuthMode: "userpools" }
                : { forwardAuthMode: "iam" };
        }
        else {
            this.options = {
                forwardAuthMode: optsOrBool?.forwardAuthMode ?? "userpools",
            };
        }
    }
    invoke(variables = {}) {
        return invokeGraphqlProxy(this.query, variables, this.options);
    }
    execute(variables = {}) {
        return this.invoke(variables);
    }
    run(variables = {}) {
        return this.invoke(variables);
    }
    fetch(variables = {}) {
        return this.invoke(variables);
    }
}
exports.GraphqlQuery = GraphqlQuery;
/* -------------------------------------------------
   Subscriptions (web-only)
--------------------------------------------------*/
async function SubscribeToAgentAPI(subscriptionQuery, variables, onNext) {
    const authMode = "AMAZON_COGNITO_USER_POOLS";
    if (isReactNative()) {
        console.warn("[subs] SubscribeToAgentAPI is not implemented for React Native in shared invoker yet. Returning a no-op subscription.");
        return { unsubscribe: () => { } };
    }
    const { API } = getWebAmplify();
    console.debug("[subs] API.graphql (public)", {
        authMode,
        variables,
        rn: isReactNative(),
    });
    const observable = API.graphql({
        query: subscriptionQuery,
        variables,
        authMode: authMode,
    });
    const subscription = observable.subscribe({
        next: ({ value }) => {
            try {
                if (value?.data)
                    onNext(value.data);
            }
            catch (e) {
                console.warn("⚠️ Subscription next handler error:", e);
            }
        },
        error: (err) => {
            console.warn("⚠️ Subscription error:", err);
        },
    });
    return {
        unsubscribe: () => {
            try {
                subscription?.unsubscribe?.();
            }
            catch (err) {
                console.warn("⚠️ Error unsubscribing:", err);
            }
        },
    };
}
